// src/components/Tutorial.jsx

import React, { useState } from 'react';
import { X, Wallet, History, BookOpen, BarChart3, MessageCircle, CheckCircle, ArrowRight } from 'lucide-react';

const Tutorial = ({ isVisible, onClose }) => {
  const [step, setStep] = useState(0);

  if (!isVisible) return null;

  // Define the tutorial steps
  const steps = [
    {
      title: "Account Overview",
      icon: <Wallet size={24} />,
      color: "text-blue-500 dark:text-blue-400", // Adjusted for light mode visibility
      bg: "bg-blue-100 dark:bg-blue-500/20",
      description: "Monitor your real-time financial health. Check your current Balance, live Equity, and Open P/L to see how your positions are performing right now."
    },
    {
      title: "Trade History",
      icon: <History size={24} />,
      color: "text-purple-500 dark:text-purple-400",
      bg: "bg-purple-100 dark:bg-purple-500/20",
      description: "View your recently closed trades. Click on any trade row to expand it and see detailed information like Entry/Exit prices, volume, and commissions."
    },
    {
      title: "Journal",
      icon: <BookOpen size={24} />,
      color: "text-amber-500 dark:text-amber-400",
      bg: "bg-amber-100 dark:bg-amber-500/20",
      description: "Improve your psychology. This section automatically analyzes your behavior, flagging issues like revenge trading, overtrading, or closing winners too early."
    },
    {
      title: "Analytics",
      icon: <BarChart3 size={24} />,
      color: "text-green-500 dark:text-green-400",
      bg: "bg-green-100 dark:bg-green-500/20",
      description: "Dive into your statistics. View advanced metrics like Profit Factor, Max Drawdown, Recovery Factor, and asset distribution charts."
    },
    {
      title: "AI Assistant",
      icon: <MessageCircle size={24} />,
      color: "text-pink-500 dark:text-pink-400",
      bg: "bg-pink-100 dark:bg-pink-500/20",
      description: "Need help? Look for the floating button in the corner. You can drag it anywhere on the screen and click to chat with the AI about your trading data."
    }
  ];

  const handleNext = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      onClose();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const currentStep = steps[step];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 dark:bg-black/80 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col h-[500px]">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/50 dark:to-purple-900/50 p-6 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Dashboard Tour</h2>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
            >
              <X size={20} />
            </button>
          </div>
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-700 h-1.5 mt-4 rounded-full overflow-hidden">
            <div 
              className="bg-blue-500 h-full transition-all duration-300 ease-out"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 flex-grow flex flex-col items-center justify-center text-center bg-slate-50 dark:bg-gray-900">
          <div className={`p-4 rounded-2xl ${currentStep.bg} ${currentStep.color} mb-6 shadow-sm`}>
            {currentStep.icon}
          </div>
          
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">{currentStep.title}</h3>
          
          <p className="text-gray-500 dark:text-gray-400 text-base leading-relaxed">
            {currentStep.description}
          </p>

          {/* Step Indicators (Dots) */}
          <div className="flex gap-2 mt-8">
            {steps.map((_, idx) => (
              <div 
                key={idx} 
                className={`h-2 rounded-full transition-all duration-300 ${idx === step ? 'w-6 bg-blue-500' : 'w-2 bg-gray-300 dark:bg-gray-700'}`}
              />
            ))}
          </div>
        </div>

        {/* Footer / Navigation */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 flex-shrink-0">
          <div className="flex gap-3">
            {step > 0 ? (
              <button
                onClick={handleBack}
                className="px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium w-1/3"
              >
                Back
              </button>
            ) : (
              <div className="w-1/3"></div> // Spacer to keep Next button aligned
            )}
            
            <button
              onClick={handleNext}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 group shadow-sm"
            >
              {step === steps.length - 1 ? 'Finish' : 'Next'}
              {step !== steps.length - 1 && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
              {step === steps.length - 1 && <CheckCircle size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tutorial;